/**
 * @file filters.tsx
 *
 * @fileoverview Renders facets in the form of filters. Each filter is a dropdown with a popover
 * that displays the elements inside.
 */

import React from 'react';
import { Facet } from '~/jobiqo/models/search';
import { FlexItems } from '~/vcl/atoms/06-layout/03-flex';
import { FlexItem } from '~/vcl/atoms/06-layout/03-flex/02-flex-item';

import FacetAutocomplete from './components/FacetAutocomplete';
import SimpleFacet from './components/SimpleFacet';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';

export interface FacetFilterButtonsProps {
  /**
   * The current querystring object.
   */
  querystring: any;
  /**
   * The current pathname.
   */
  pathname: string;
  /**
   * The list of facets.
   */
  facets: Facet[];
  /**
   * The callback for when toggling a filter.
   */
  onFacetToggle: (
    name: string,
    selectedItems: string[],
    alias?: string,
  ) => void;
  /**
   * Gets the list of selected facets.
   */
  getFacetSelectedItems(facet: Facet): string[];
  /**
   * Renders additional filters.
   */
  renderAdditionalFilters?: () => React.ReactNode;
}

export const FacetFilterButtons = ({
  facets,
  querystring,
  pathname,
  onFacetToggle,
  getFacetSelectedItems,
  renderAdditionalFilters,
}: FacetFilterButtonsProps) => {
  const { APP } = useFrontendConfig();

  return (
    <FlexItems justifyContent="center">
      {facets.map((facet, index) => {
        return (
          <FlexItem data-id={facet.name} key={facet.name} margin={4}>
            {facet.values.length >= APP.FACET_ITEMS_AUTOCOMPLETE_THRESHOLD ? (
              <FacetAutocomplete
                index={index}
                querystring={querystring}
                pathname={pathname}
                facets={facets}
                facet={facet}
                onFacetToggle={onFacetToggle}
                getFacetSelectedItems={getFacetSelectedItems}
              />
            ) : (
              <SimpleFacet
                querystring={querystring}
                pathname={pathname}
                facet={facet}
                onFacetToggle={onFacetToggle}
                getFacetSelectedItems={getFacetSelectedItems}
              />
            )}
          </FlexItem>
        );
      })}
      {renderAdditionalFilters && renderAdditionalFilters()}
    </FlexItems>
  );
};
